/**
 * StudentProgress - Student list view with progress data
 *
 * Features:
 * - Table: Student name | Assessments | Avg Score | Mastery bar
 * - Click row to expand student detail
 * - Color-coded mastery levels (green/yellow/red)
 */

import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ChevronDown,
  ChevronUp,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  HelpCircle,
} from "lucide-react";
import { MasteryBadge, getMasteryTextColor } from "./MasteryBadge";
import { StudentDetailCard } from "./StudentDetailCard";
import type { StudentProgress as StudentProgressType, StudentDetail, TrendDirection } from "@/services/progress.service";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

export interface MergedStudentProgress extends StudentProgressType {
  studentName: string;
  studentId: string | null;
  isUnknown: boolean;
}

export interface StudentProgressProps {
  /** Student progress data (merged with local names) */
  items: MergedStudentProgress[];
  /** Whether data is loading */
  loading: boolean;
  /** Student detail data for expanded view */
  expandedStudentDetail: StudentDetail | null;
  /** Loading state for student detail */
  detailLoading: boolean;
  /** Currently expanded student sheet number */
  expandedSheetNumber: string | null;
  /** Called when a student row is clicked */
  onStudentClick: (sheetNumber: string) => void;
  /** Called to close the detail view */
  onDetailClose: () => void;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Trend Icon Component
// =============================================================================

interface TrendIconProps {
  trend: TrendDirection;
  className?: string;
}

function TrendIcon({ trend, className }: TrendIconProps) {
  const config: Record<TrendDirection, { icon: React.ElementType; className: string }> = {
    improving: { icon: TrendingUp, className: 'text-green-500' },
    declining: { icon: TrendingDown, className: 'text-red-500' },
    stable: { icon: Minus, className: 'text-blue-500' },
    insufficient_data: { icon: HelpCircle, className: 'text-muted-foreground' },
  };

  const { icon: Icon, className: trendClassName } = config[trend];

  return <Icon className={cn('h-4 w-4', trendClassName, className)} />;
}

// =============================================================================
// Skeleton Loading
// =============================================================================

function StudentProgressSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 p-3 border rounded-lg">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Empty State
// =============================================================================

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <p className="text-muted-foreground font-medium">No student data</p>
      <p className="text-sm text-muted-foreground mt-1">
        Scan worksheets to see student progress
      </p>
    </div>
  );
}

// =============================================================================
// Student Row Component
// =============================================================================

interface StudentRowProps {
  student: MergedStudentProgress;
  isExpanded: boolean;
  onClick: () => void;
}

function StudentRow({ student, isExpanded, onClick }: StudentRowProps) {
  return (
    <TableRow
      className={cn(
        'cursor-pointer hover:bg-muted/50 transition-colors',
        isExpanded && 'bg-muted/50'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-expanded={isExpanded}
    >
      {/* Student Name */}
      <TableCell>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0',
              student.isUnknown
                ? 'bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary'
            )}
          >
            {student.studentName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className={cn('font-medium truncate', student.isUnknown && 'text-muted-foreground')}>
              {student.studentName}
            </p>
            {/* Mobile: show inline stats */}
            <p className="text-xs text-muted-foreground sm:hidden">
              {student.totalAssessments} assessments - {Math.round(student.avgPercentage)}%
            </p>
          </div>
        </div>
      </TableCell>

      {/* Assessments (hidden on mobile) */}
      <TableCell className="hidden sm:table-cell text-center">
        <span className="font-medium">{student.totalAssessments}</span>
      </TableCell>

      {/* Avg Score (hidden on mobile) */}
      <TableCell className="hidden sm:table-cell">
        <span className={cn('font-medium', getMasteryTextColor(student.avgPercentage))}>
          {Math.round(student.avgPercentage)}%
        </span>
      </TableCell>

      {/* Mastery Bar (hidden on mobile) */}
      <TableCell className="hidden md:table-cell w-40">
        <Progress
          value={student.avgPercentage}
          className="h-2"
          style={{
            '--progress-indicator-color': student.avgPercentage >= 80
              ? 'rgb(34 197 94)'
              : student.avgPercentage >= 50
              ? 'rgb(234 179 8)'
              : 'rgb(239 68 68)',
          } as React.CSSProperties}
        />
      </TableCell>

      {/* Trend (hidden on mobile) */}
      <TableCell className="hidden lg:table-cell">
        <TrendIcon trend={student.recentTrend} />
      </TableCell>

      {/* Mastery Badge */}
      <TableCell>
        <div className="flex items-center gap-2 justify-end">
          <MasteryBadge percentage={student.avgPercentage} size="sm" className="hidden sm:inline-flex" />
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function StudentProgress({
  items,
  loading,
  expandedStudentDetail,
  detailLoading,
  expandedSheetNumber,
  onStudentClick,
  onDetailClose,
  className,
}: StudentProgressProps) {
  // Find the expanded student for display name
  const expandedStudent = useMemo(() => {
    if (!expandedSheetNumber) return null;
    return items.find((s) => s.sheetNumber === expandedSheetNumber) || null;
  }, [items, expandedSheetNumber]);

  if (loading) {
    return <StudentProgressSkeleton />;
  }

  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className={cn('overflow-x-auto', className)}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead className="hidden sm:table-cell text-center w-24">Assessments</TableHead>
              <TableHead className="hidden sm:table-cell w-20">Avg Score</TableHead>
              <TableHead className="hidden md:table-cell w-40">Progress</TableHead>
              <TableHead className="hidden lg:table-cell w-16">Trend</TableHead>
              <TableHead className="w-24 text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((student) => (
              <StudentRow
                key={student.sheetNumber}
                student={student}
                isExpanded={expandedSheetNumber === student.sheetNumber}
                onClick={() => onStudentClick(student.sheetNumber)}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Student Detail Dialog */}
      <Dialog open={!!expandedSheetNumber} onOpenChange={(open: boolean) => !open && onDetailClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Student Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="space-y-4 mt-6">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : expandedStudentDetail && expandedStudent ? (
            <StudentDetailCard
              data={expandedStudentDetail}
              studentName={expandedStudent.studentName}
              isUnknown={expandedStudent.isUnknown}
              className="border-0 shadow-none"
            />
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <p>Unable to load student details</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default StudentProgress;
